import { CommonModule, } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ElementRef, HostListener } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RouteService } from '../../../services/route.service';
import DataTableConfig, { DataTableColumn, DataTableTopInputFilter } from './data-table.config';
import { DataTableColumnTextComponent } from "./columns/data-table-column-text/data-table-column-text.component";
import { DataTableColumnText } from './columns/data-table-column-text/data-table-column-text.config';
import { DataTableColumnActions, DataTableColumnActionsButton } from './columns/data-table-column-actions/data-table-column-actions.config';
import { DataTableColumnActionsComponent } from './columns/data-table-column-actions/data-table-column-actions.component';
import { DataTableColumnIcon } from './columns/data-table-column-icon/data-table-column-icon.config';
import { DataTableColumnIconComponent } from './columns/data-table-column-icon/data-table-column-icon.component';
import { DataTableColumnWaitingTimeComponent } from './columns/data-table-column-waiting-time/data-table-column-waiting-time.component';
import { DataTableColumnWaitingTime } from './columns/data-table-column-waiting-time/data-table-column-waiting-time.config';
import { DataTableColumnBadge } from './columns/data-table-column-badge/data-table-column-badge.config';
import { DataTableColumnBadgeComponent } from './columns/data-table-column-badge/data-table-column-badge.component';
import { DataTableColumnLink } from './columns/data-table-column-link/data-table-column-link.config';
import { DataTableColumnLinkComponent } from './columns/data-table-column-link/data-table-column-link.component';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { OptionService } from '../../../services/api/option.service';
import { CommonService } from 'src/app/services/common.service';
import { InputFieldComponent } from '../../form/input-field/input-field.component';
import { AccessControlService } from 'src/app/services/access-control.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { AccessAction, FeatureActionKey } from 'src/app/layouts/main-layout/components/sidebar/config/access-control.config';
import { stripAppBase } from 'src/app/app-paths';


@Component({
  selector: 'data-table',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputFieldComponent, DataTableColumnTextComponent, DataTableColumnActionsComponent, DataTableColumnIconComponent, DataTableColumnWaitingTimeComponent, DataTableColumnBadgeComponent, DataTableColumnLinkComponent],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss'
})
export class DataTableComponent implements OnInit, OnDestroy {

  @Output() onPageSizeChange = new EventEmitter<any>();
  @Output() onPageChange = new EventEmitter<number>();
  @Output() onFilterChange = new EventEmitter<any>();
  @Output() onSortChange = new EventEmitter<{ sortField: string | null; sortOrder: 'asc' | 'desc' }>();
  @Output() onExportExcel = new EventEmitter<void>();

  @Input() config?: DataTableConfig;
  @Input() service?: any;
  @Input() idKey: string = 'id';
  @Input() subSectionSettings?: string;
  @Input() currentPage: number = 0;
  @Input() pageSize: number = 25;
  @Input() sortField: string | null = null;
  @Input() sortOrder: 'asc' | 'desc' = 'asc';
  @Input() showExportButton: boolean = false;
  @Input() isExporting: boolean = false;

  pageSizeOptions = [25, 50, 100];

  itemToDelete: any;

  resizingColumn: any = null;
  startX: number = 0;
  startWidth: number = 0;

  filterTimeout: any;
  autocompleteOptions: { [key: string]: any[] } = {};

  private uniqueOptions: { [key: string]: any[] } = {};
  public dynamicOptions: { [key: string]: any[] } = {};
  public multiSelectOpenColumn: string | null = null;
  public selectedMultiSelectValues: { [key: string]: any[] } = {};
  public multiSelectDropdownStyle: { [key: string]: string } = {};

  currentFilterValues: { [key: string]: any } = {};
  topInputControls: { [key: string]: FormControl } = {};

  Math = Math;
  private subscriptions = new Subscription();
  private activeProfileCode: string | null = null;

  private readonly routeToFeatureActionMap: Array<[string, FeatureActionKey]> = [
    ['/clients/settings/client-types', 'CLIENTS_SETTINGS_CLIENT_TYPES'],
    ['/requests/settings/request-categories', 'REQUESTS_SETTINGS_CATEGORIES'],
    ['/proposals/settings/proposal-steps', 'PROPOSALS_SETTINGS_PROPOSAL_STEPS'],
    ['/proposals/settings/payment-terms', 'PROPOSALS_SETTINGS_PAYMENT_TERMS'],
    ['/projects/settings/checklist', 'PROJECTS_SETTINGS_CHECKLIST'],
    ['/projects/settings/testlist', 'PROJECTS_SETTINGS_TESTLIST'],
    ['/general-settings/categories', 'GS_CATEGORY'],
    ['/general-settings/regulations', 'GS_REGULATION'],
    ['/general-settings/laws', 'GS_LAW'],
    ['/general-settings/jurisdictions', 'GS_JURISDICTION'],
    ['/general-settings/items', 'GS_ITEMS'],
    ['/general-settings/item-types', 'GS_ITEM_TYPE'],
    ['/general-settings/users', 'GS_USERS'],
    ['/general-settings/companies', 'GS_COMPANIES'],
    ['/general-settings/departments', 'GS_DEPARTMENTS'],
    ['/hr/employees', 'HR_EMPLOYEES'],
    ['/hr/specialisations', 'HR_SPECIALISATION'],
    ['/marketing/campaigns', 'MARKETING_CAMPAIGNS'],
    ['/quality-management/activities-planning', 'QUALITY_MANAGEMENT_ACTIVITIES_PLANNING'],
    ['/quality-management/settings/quality-document-group', 'QUALITY_MANAGEMENT_SETTINGS_DOCUMENT_GROUPS'],
    ['/quality-management/company-documents', 'QUALITY_MANAGEMENT_COMPANY_DOCUMENTS'],
    ['/clients', 'CLIENTS_LIST'],
    ['/requests', 'REQUESTS_LIST'],
    ['/proposals', 'PROPOSALS_LIST'],
    ['/projects', 'PROJECTS_LIST'],
    ['/introducers', 'INTRODUCER_LIST'],
    ['/regulators', 'REGULATORS_LIST']
  ];

  constructor(
    private routeService: RouteService,
    private optionsService: OptionService,
    private el: ElementRef,
    private commonService: CommonService,
    private confirmationDialogService: ConfirmationDialogService,
    private accessControlService: AccessControlService,
    private mainLayoutService: MainLayoutService,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe((profileCode) => {
        this.activeProfileCode = profileCode;
      })
    );

    if (this.config?.data && this.config?.columns) {
      this.generateUniqueOptions();
      this.getOptionsColumn();         
      this.initializeFilterValues();
      this.initializeMultiSelectValues();
    }
    if (this.config?.topInputFilters?.length) {
      this.initializeTopInputFilters();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  
  @HostListener('window:resize')
  onResize(): void {
    this.closeMultiSelectDropdowns();
    setTimeout(() => {
    }, 100);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.closeMultiSelectDropdowns();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.multiSelectOpenColumn && !this.el.nativeElement.contains(event.target)) {
      this.closeMultiSelectDropdowns();
    }
  }

  initializeFilterValues(): void {
    if (this.config?.columns) {
      this.config.columns.forEach(column => {
        this.currentFilterValues[column.attribute] = column.defaultFilterValue ?? null;
      });
    }
  }

  isSelectedFilterOption(column: DataTableColumn, optionKey: any): boolean {
    const currentValue = this.currentFilterValues[column.attribute];

    if (currentValue === null || currentValue === undefined || currentValue === '') {
      return false;
    }

    return String(currentValue) === String(optionKey);
  }

  initializeMultiSelectValues(): void {
    if (!this.config?.columns) return;

    this.config.columns.forEach(column => {
      if (column.filter !== 'multiselect') return;

      const existingValue = this.config?.searchModel?.[column.attribute];
      this.selectedMultiSelectValues[column.attribute] = existingValue
        ? String(existingValue).split(',').filter(v => v.length > 0)
        : [];
    });
  }

  toggleMultiSelectDropdown(column: DataTableColumn, event?: Event): void {
    if (this.multiSelectOpenColumn === column.attribute) {
      this.closeMultiSelectDropdowns();
      return;
    }

    const toggle = event?.currentTarget as HTMLElement | undefined;
    if (toggle) {
      const rect = toggle.getBoundingClientRect();
      this.multiSelectDropdownStyle = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        'min-width': `${rect.width}px`
      };
    }
    this.multiSelectOpenColumn = column.attribute;
  }

  isMultiSelectOptionChecked(column: DataTableColumn, optionKey: any): boolean {
    return (this.selectedMultiSelectValues[column.attribute] ?? []).includes(optionKey);
  }

  onMultiSelectOptionToggle(column: DataTableColumn, optionKey: any, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const current = this.selectedMultiSelectValues[column.attribute] ?? [];

    this.selectedMultiSelectValues[column.attribute] = checked
      ? [...current, optionKey]
      : current.filter(key => key !== optionKey);

    if (this.config && this.config.searchModel) {
      this.config.searchModel[column.attribute] = this.selectedMultiSelectValues[column.attribute].join(',');
    }

    this.onFilterChange.emit(this.config?.searchModel);
  }

  getMultiSelectLabel(column: DataTableColumn): string {
    const selected = this.selectedMultiSelectValues[column.attribute] ?? [];
    const options = this.dynamicOptions[column.attribute] ?? [];

    if (selected.length === 0 || (options.length > 0 && selected.length >= options.length)) {
      return 'All';
    }
    if (selected.length === 1) {
      return options.find(option => option.key === selected[0])?.value ?? selected[0];
    }
    return `${selected.length} selected`;
  }

  closeMultiSelectDropdowns(): void {
    this.multiSelectOpenColumn = null;
  }

  initializeTopInputFilters(): void {
    this.topInputControls = {};

    if (!this.config?.topInputFilters?.length) {
      return;
    }

    this.config.topInputFilters.forEach((filter) => {
      const control = new FormControl(filter.defaultValue ?? '');
      this.topInputControls[filter.attribute] = control;

      if (filter.defaultValue !== undefined && filter.defaultValue !== null) {
        this.applyTopInputFilterValue(filter, filter.defaultValue);
      }

      control.valueChanges.subscribe((value) => {
        this.applyTopInputFilterValue(filter, value);
      });
    });
  }

  applyTopInputFilterValue(filter: DataTableTopInputFilter, value: any): void {
    if (!this.config?.searchModel) {
      return;
    }

    if (filter.type === 'date') {
      const rawValue = typeof value === 'string' ? value.trim() : value;

      if (!rawValue) {
        this.config.searchModel[filter.attribute] = '';
        this.onFilterChange.emit(this.config.searchModel);
        return;
      }

      if (typeof rawValue !== 'string' || !this.isCompleteDateString(rawValue)) {
        this.config.searchModel[filter.attribute] = '';
        return;
      }

      const normalizedValue = this.formatDateForFilter(rawValue);
      if (!normalizedValue) {
        return;
      }

      this.config.searchModel[filter.attribute] = normalizedValue;
      this.onFilterChange.emit(this.config.searchModel);
      return;
    }

    this.config.searchModel[filter.attribute] = value ?? '';
    this.onFilterChange.emit(this.config.searchModel);
  }

  private isCompleteDateString(value: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return false;
    }

    const [yearString, monthString, dayString] = value.split('-');
    const year = Number(yearString);
    const month = Number(monthString);
    const day = Number(dayString);

    if (year < 1900 || year > 2100) {
      return false;
    }

    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }

  changePage(page: number) {
    if (this.config && this.config.pagination) {
      if (page >= 0 && page < this.config.pagination.totalPages) {
        this.onPageChange.emit(page);
      }
    }
  }

  isEven(index: number): boolean {
    return index % 2 === 0;
  }
  
  private paginationContainerSelector = '.primary-pagination';
  private averagePageItemWidth = 42; 
  private navigationButtonsWidth = 100;
  private getMaxVisiblePageItems(): number {
    try {
      const paginationContainer = this.el.nativeElement.querySelector(this.paginationContainerSelector);
      if (!paginationContainer) {
        return 7;
      }
      
      const containerWidth = paginationContainer.clientWidth - this.navigationButtonsWidth;
      
      const maxItems = Math.floor(containerWidth / this.averagePageItemWidth);
      
      return Math.max(5, maxItems);
    } catch (error) {
      console.error('Error calculating visible elements of paging:', error);
      return 7; 
    }
  }

  shouldUseCompactPagination(): boolean {
    if (!this.config || !this.config.pagination) {
      return false;
    }
    const maxVisibleItems = this.getMaxVisiblePageItems();
    return this.config.pagination.totalPages > maxVisibleItems;
  }
  
  getAllPages(): number[] {
    if (!this.config || !this.config.pagination) {
      return [];
    }
    
    const result: number[] = [];
    for (let i = 0; i < this.config.pagination.totalPages; i++) {
      result.push(i);
    }
    return result;
  }

  getVisiblePages(): number[] {
    if (!this.config || !this.config.pagination) {
      return [];
    }
    
    const totalPages = this.config.pagination.totalPages;
    const currentPage = this.currentPage;
    
    const maxVisibleItems = this.getMaxVisiblePageItems();
    
    const availableForWindow = Math.max(1, maxVisibleItems - 3);
    
    const pagesWindow = Math.floor(availableForWindow / 2);
    
    const result: number[] = [];
    
    let startPage = Math.max(1, currentPage - pagesWindow);
    let endPage = Math.min(totalPages - 2, currentPage + pagesWindow);
    
    if (startPage <= 1) {
      endPage = Math.min(totalPages - 2, 1 + (pagesWindow * 2));
    }
    if (endPage >= totalPages - 2) {
      startPage = Math.max(1, totalPages - 2 - (pagesWindow * 2));
    }
    
    for (let i = startPage; i <= endPage; i++) {
      if (i > 0 && i < totalPages - 1) {
        result.push(i);
      }
    }
    
    return result;
  }
  
  filter(column: DataTableColumn, event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    this.currentFilterValues[column.attribute] = value;

    if (column.filter === 'text') {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        if (this.config && this.config.searchModel) {
          this.config.searchModel[column.attribute] = value;
        }
        this.onFilterChange.emit(this.config?.searchModel);
      }, 500);
    } else if (column.filter === 'select') {
      if (this.config && this.config.searchModel) {
        this.config.searchModel[column.attribute] = value;
      }
      this.onFilterChange.emit(this.config?.searchModel);
      
      this.updateDependentFilters(column.attribute, value);
    } else if (column.filter === 'autocomplete') {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.fetchAutocompleteOptions(column.attribute, value);
      }, 500);
    }
  }

  filterRange(column: DataTableColumn, bound: 'min' | 'max', event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    const attribute = bound === 'min' ? column.filterMinAttribute : column.filterMaxAttribute;
    if (!attribute) {
      return;
    }

    this.currentFilterValues[attribute] = value;

    if (this.config && this.config.searchModel) {
      this.config.searchModel[attribute] = value;
    }

    clearTimeout(this.filterTimeout);
    this.filterTimeout = setTimeout(() => {
      this.onFilterChange.emit(this.config?.searchModel);
    }, 500);
  }

  isSortable(column: DataTableColumn): boolean {
    return column.sortable !== false;
  }

  fetchAutocompleteOptions(attribute: string, query: string) {

    this.service?.getAutocompleteOptions(attribute, query).subscribe((options: any[]) => {
      this.autocompleteOptions[attribute] = options;
    });
  }

  selectAutocompleteOption(column: DataTableColumn, option: any) {
    if (this.config && this.config.searchModel) {
      this.config.searchModel[column.attribute] = option;
    }
    this.onFilterChange.emit(this.config?.searchModel);
  }

  changePageSize(event: Event) {
    const target = event.target as HTMLSelectElement
    this.onPageSizeChange.emit(this.pageSizeOptions[target.selectedIndex]);
  }

  onSort(field: string) {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortOrder = 'asc';
    }
    this.onSortChange.emit({ sortField: this.sortField, sortOrder: this.sortOrder });
  }

  isDataTableColumnText(column: DataTableColumn) {
    return column instanceof DataTableColumnText;
  }
  asDataTableColumnText(column: DataTableColumn) {
    return column as DataTableColumnText;
  }

  isDataTableColumnActions(column: DataTableColumn) {
    return column instanceof DataTableColumnActions;
  }
  asDataTableColumnActions(column: DataTableColumn) {
    return column as DataTableColumnActions;
  }

  getVisibleActionsColumn(column: DataTableColumnActions): DataTableColumnActions {
    return new DataTableColumnActions({
      ...column,
      buttons: (column.buttons ?? []).filter((button) => this.isActionButtonVisible(button))
    });
  }

  private isActionButtonVisible(button: DataTableColumnActionsButton): boolean {
    const featureKey = this.resolveActionFeatureKey();
    if (!featureKey) {
      return true;
    }

    if (featureKey === 'PROPOSALS_LIST' && this.isExportButton(button)) {
      return true;
    }

    const action = this.resolveButtonAction(button);
    if (!action) {
      return true;
    }

    return this.accessControlService.hasFeatureActionAccess(this.activeProfileCode, featureKey, action);
  }

  private resolveActionFeatureKey(): FeatureActionKey | null {
    const path = stripAppBase(this.router.url).split('?')[0].toLowerCase();
    for (const [prefix, featureKey] of this.routeToFeatureActionMap) {
      if (path.startsWith(prefix)) {
        return featureKey;
      }
    }
    return null;
  }

  private resolveButtonAction(button: DataTableColumnActionsButton): AccessAction | null {
    if (button.aclAction) {
      return button.aclAction;
    }

    if (button.isDelete) {
      return 'delete';
    }

    const icon = (button.icon ?? '').toLowerCase();
    const tooltip = (button.tooltip ?? '').toLowerCase();

    if (icon.includes('trash') || tooltip.includes('delete')) {
      return 'delete';
    }

    if (icon.includes('eye') || tooltip.includes('view') || tooltip.includes('detail')) {
      return 'view';
    }

    if (icon.includes('edit') || icon.includes('pencil') || icon.includes('pen') || tooltip.includes('edit')) {
      return 'modify';
    }

    return null;
  }

  private isExportButton(button: DataTableColumnActionsButton): boolean {
    const icon = (button.icon ?? '').toLowerCase();
    const tooltip = (button.tooltip ?? '').toLowerCase();
    return icon.includes('export') || tooltip.includes('export');
  }

  // Data table column icons
  isDataTableColumnIcons(column: DataTableColumn) {
    return column instanceof DataTableColumnIcon;
  }
  asDataTableColumnIcons(column: DataTableColumn) {
    return column as DataTableColumnIcon;
  }

  // Data table column waiting time
  isDataTableColumnWaitingTime(column: DataTableColumn) {
    return column instanceof DataTableColumnWaitingTime;
  }
  asDataTableColumnWaitingTime(column: DataTableColumn) {
    return column as DataTableColumnWaitingTime;
  }

  isDataTableColumnBadge(column: DataTableColumn) {
    return column instanceof DataTableColumnBadge;
  }
  asDataTableColumnBadge(column: DataTableColumn) {
    return column as DataTableColumnBadge;
  }

  isDataTableColumnLink(column: DataTableColumn) {
    return column instanceof DataTableColumnLink;
  }
  asDataTableColumnLink(column: DataTableColumn) {
    return column as DataTableColumnLink;
  }



  /********************
   Confirm delete
   ******************/

  confirmDelete(item: any) {
    this.itemToDelete = item;
    const message = `Are you sure to delete ${this.idKey.replace(/Id$/, '')}?`;
    this.confirmationDialogService.open(
      message,
      () => this.deleteItem()
    );
  }

  deleteItem() {
    if (this.subSectionSettings && typeof this.service[this.subSectionSettings] === 'function') {
        this.service[this.subSectionSettings](this.itemToDelete[this.idKey]).subscribe((response: any) => {
      if (this.config?.data) {
        this.config.data = this.config.data.filter(item => item[this.idKey] !== this.itemToDelete[this.idKey]);
      }
      this.itemToDelete = null;
    });
    } else {
      this.service.delete(this.itemToDelete[this.idKey]).subscribe((response: any) => {
        if (this.config?.data) {
          this.config.data = this.config.data.filter(item => item[this.idKey] !== this.itemToDelete[this.idKey]);
        }
        this.itemToDelete = null;
      });
    }
  }

  onResizeStart(event: MouseEvent, column: any): void {
    event.preventDefault();

    this.resizingColumn = column;
    this.startX = event.clientX;


    const columnElement = event.target as HTMLElement;
    this.startWidth = columnElement.offsetWidth;

    const mouseMoveListener = (moveEvent: MouseEvent) => this.onResizing(moveEvent);
    const mouseUpListener = () => {
      this.onResizeEnd();
      document.removeEventListener('mousemove', mouseMoveListener);
      document.removeEventListener('mouseup', mouseUpListener);
    };

    document.addEventListener('mousemove', mouseMoveListener);
    document.addEventListener('mouseup', mouseUpListener);
  }

  onResizing(event: MouseEvent): void {
    if (this.resizingColumn) {
      const deltaX = event.clientX - this.startX;
      const newWidth = Math.max(this.startWidth + deltaX, 50);
      this.resizingColumn.width = `${newWidth}px`;
    }
  }

  onResizeEnd(): void {
    this.resizingColumn = null;
    this.clearAllColumnBorders();
  }

  clearAllColumnBorders(): void {
    const table = this.el.nativeElement.querySelector('table');
    if (table) {
      const cells = table.querySelectorAll('td, th');
      cells.forEach((cell: HTMLElement) => {
        cell.style.borderRight = '';
      });
    }
  }

  onMouseOverResizer(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    target.style.cursor = 'col-resize';
  }

  onMouseOutResizer(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    target.style.cursor = 'default';
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    const target = event.target as HTMLElement;


    if ((target.classList.contains('dtr-control') || target.classList.contains('sorting')) && target.classList.contains('resizer')) {
      const rect = target.getBoundingClientRect();
      const isNearRightEdge = event.clientX > rect.right - 5 && event.clientX <= rect.right;

      if (isNearRightEdge) {
        target.style.cursor = 'col-resize';
        this.highlightColumnBorders(target, true);
      } else {
        target.style.cursor = this.resizingColumn ? 'col-resize' : 'default';
        this.highlightColumnBorders(target, false);
      }
    } else {
      target.style.cursor = this.resizingColumn ? 'col-resize' : 'default';
      this.highlightColumnBorders(target, false);
    }
  }

  highlightColumnBorders(target: HTMLElement, highlight: boolean): void {
    const table = this.el.nativeElement.querySelector('table');
    const columnIndex = Array.from(target.parentElement!.children).indexOf(target);

    if (table) {
      const rows = table.querySelectorAll('tr');


      rows.forEach((row: HTMLElement) => {
        const cells = row.children;
        Array.from(cells).forEach((cell) => {
          const htmlCell = cell as HTMLElement;
          htmlCell.style.borderRight = '';
        });
      });


      if (highlight) {
        rows.forEach((row: HTMLElement) => {
          const cells = row.children;
          if (cells[columnIndex]) {
            const cell = cells[columnIndex] as HTMLElement;
            cell.style.borderRight = '2px solid var(--bs-primary)';
          }
        });
      }
    }
  }

  private getOptionsColumn(): any {
    setTimeout(() => {
      if (this.config?.data && this.config?.columns) {
        this.config?.columns.forEach((column: DataTableColumn) => {
          const value = column?.filterOptionsSource;
          if (value !== undefined && value !== null) {
            this.optionsService[value]().subscribe(
              (response: any) => {
                const options = response?.data?.options ?? response?.options;
                if (options) {
                  this.dynamicOptions[column.attribute] = options.map((option: any) => option);
                }
              },
              (error: any) => {
                console.error(`Errore durante il recupero delle opzioni per ${column.filterOptionsSource}:`, error);
              }
            );
          }

        });
      }
    }, 500);
  }

  private generateUniqueOptions(): void {
    setTimeout(() => {
      if (this.config?.data && this.config?.columns) {
        this.config?.columns.forEach((column: DataTableColumn) => {
          const uniqueValues = new Set<any>();
          this.config?.data?.forEach(item => {
            const value = item[column.attribute];
            if (value !== undefined && value !== null) {
              uniqueValues.add(value);
            }
          });
          this.uniqueOptions[column.attribute] = Array.from(uniqueValues).sort();
        });
      }
    }, 500);
  }

  getUniqueOptions(column: DataTableColumn): any[] {
    return this.uniqueOptions[column.attribute] || [];
  }

  checkDateFormat(column: DataTableColumn, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    
    if (!value) {
      if (this.config && this.config.searchModel) {
        this.config.searchModel[column.attribute] = '';
        this.onFilterChange.emit(this.config?.searchModel);
      }
      return;
    }
    
    const dateParts = value.split('-');
    if (dateParts.length !== 3) return; 
    
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const day = parseInt(dateParts[2], 10);
    
    if (year >= 1900 && year <= 2100 && 
        month >= 1 && month <= 12 && 
        day >= 1 && day <= 31 && 
        value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      
      if (this.config && this.config.searchModel) {
        this.config.searchModel[column.attribute] = this.formatDateForFilter(value);
      }
      this.onFilterChange.emit(this.config?.searchModel);
    }
  }

  
  formatDateForFilter(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ''; 
      
      return this.commonService.formatDateOnly(date);
    } catch (error) {
      console.error('Errore nella formattazione della data:', error);
      return '';
    }
  }

  updateDependentFilters(sourceAttribute: string, value: any): void {
    if (!this.config?.columns) return;
    
    const dependentColumns = this.config.columns.filter(col => {
      if (col instanceof DataTableColumnText && col.dependsOn) {
        return col.dependsOn.column === sourceAttribute;
      }
      return false;
    }) as DataTableColumnText[];
    
    dependentColumns.forEach(dependentColumn => {
      if (!dependentColumn.dependsOn || !dependentColumn.dependsOn.fetchOptionsMethod) return;
      
      if (!value) {
        if (dependentColumn.filterOptionsSource) {
          this.getOptionsForColumn(dependentColumn);
        } else {
          this.generateUniqueOptionsForColumn(dependentColumn);
        }
        
        if (this.config && this.config.searchModel) {
          this.config.searchModel[dependentColumn.attribute] = null;
          this.currentFilterValues[dependentColumn.attribute] = null;
        }
        return;
      }
      
      const methodName = dependentColumn.dependsOn.fetchOptionsMethod;
      
      if (this.service && typeof this.service[methodName] === 'function') {
        this.service[methodName](value).subscribe(
          (response: any) => {
            if (response?.data?.options) {
              this.dynamicOptions[dependentColumn.attribute] = response.data.options;
            } else if (Array.isArray(response)) {
              this.dynamicOptions[dependentColumn.attribute] = response;
            }
          },
          (error: any) => {
            console.error(`Errore durante il recupero delle opzioni per ${dependentColumn.attribute}:`, error);
          }
        );
      }
    });
  }
  
  getOptionsForColumn(column: DataTableColumn): void {
    if (!column.filterOptionsSource) return;
    
    this.optionsService[column.filterOptionsSource]().subscribe(
      (response: any) => {
        const options = response?.data?.options ?? response?.options;
        if (options) {
          this.dynamicOptions[column.attribute] = options.map((option: any) => option);
        }
      },
      (error: any) => {
        console.error(`Errore durante il recupero delle opzioni per ${column.filterOptionsSource}:`, error);
      }
    );
  }
  
  generateUniqueOptionsForColumn(column: DataTableColumn): void {
    if (!this.config?.data) return;
    
    const uniqueValues = new Set<any>();
    this.config.data.forEach(item => {
      const value = item[column.attribute];
      if (value !== undefined && value !== null) {
        uniqueValues.add(value);
      }
    });
    this.uniqueOptions[column.attribute] = Array.from(uniqueValues).sort();
  }
}